import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Header from '../components/layout/Header'
import { BookOpen, Clock, Coffee, Wifi, Monitor, Users, Search, MapPin, CheckCircle2, AlertCircle, QrCode } from 'lucide-react'
import UserService from '../services/api/userService'
import LibraryService from '../services/api/libraryService'
import SeatLayout from '../components/library/SeatLayout'
import QrScannerModal from '../components/ui/QrScannerModal'
import { useModal } from '../context/ModalContext'

const Library = () => {
  const location = useLocation()
  const [allSeatsData, setAllSeatsData] = useState(null) // API'den gelen tüm veri
  const [seats, setSeats] = useState({}) // Seçili seansın verisi
  const [duyurular, setDuyurular] = useState([])
  const [lockedHalls, setLockedHalls] = useState([])
  const [selectedHall, setSelectedHall] = useState(1)
  const [selectedSeat, setSelectedSeat] = useState(null)
  const [selectedSession, setSelectedSession] = useState('09:00:00-12:00:00')
  const [bookingStatus, setBookingStatus] = useState(null) // 'success' | 'error' | null
  const [userReservations, setUserReservations] = useState([])
  const [isQrModalOpen, setIsQrModalOpen] = useState(false)
  const [scanningContext, setScanningContext] = useState(null) // { id, randevuIslem }
  const { show } = useModal()

  // Seanslar sabit kalsın (kullanıcı isteği üzerine)
  const sessions = ['09:00:00-12:00:00', '12:00:00-15:00:00', '15:00:00-18:00:00', '18:00:00-21:00:00']

  const getData = async () => {
    // Paralel veri çekimi
    const [seatsRawData, reservations] = await Promise.all([LibraryService.getAllSeats(), LibraryService.getUserReservations()])

    if (seatsRawData) {
      setDuyurular(seatsRawData.duyurular || [])
      setLockedHalls(seatsRawData.locked_halls || [])
      setAllSeatsData(seatsRawData)
      if (seatsRawData[selectedSession]) {
        setSeats(seatsRawData[selectedSession])
      }
    }

    if (reservations) {
      setUserReservations(reservations)
    }
  }

  // Seçili seans değiştiğinde veya tüm veri güncellendiğinde 'seats' i güncelle
  useEffect(() => {
    if (allSeatsData) {
      // API'den gelen key'ler bazen farklı formatta olabiliyor, normalize edip bulalım
      const normalizedSelected = selectedSession.replace(/\s/g, '')
      const foundKey = Object.keys(allSeatsData).find((key) => key.replace(/\s/g, '') === normalizedSelected)

      const sessionSeats = foundKey ? allSeatsData[foundKey] : {}
      setSeats(sessionSeats)

      // Salon seçimi: kullanıcının seçtiği salon korunur, sadece dolu olduğunda diğerine geç
      setSelectedHall((prev) => {
        if (LibraryService.isHallFull(sessionSeats, prev)) {
          const other = prev === 1 ? 2 : 1
          if (!LibraryService.isHallFull(sessionSeats, other)) return other
        }
        return prev
      })
    } else {
      setSeats({})
    }

    setSelectedSeat(null)
    setBookingStatus(null)
  }, [selectedSession, allSeatsData])

  useEffect(() => {
    getData()
  }, [])

  const handleSeatClick = (seatId) => {
    setSelectedSeat(seatId)
    setBookingStatus(null)
  }

  const handleReserve = async () => {
    if (!selectedSeat) return

    // API'nin beklediği "HH:MM - HH:MM" formatına dönüştürelim
    const formattedSaat = selectedSession
      .split('-')
      .map((s) => s.substring(0, 5))
      .join(' - ')

    const reservationData = {
      data: [
        {
          id: (userReservations?.length || 0) + 1,
          saat: formattedSaat,
          masaNo: selectedSeat,
          kutuphaneId: location.state?.selectedItem?.id || 1,
        },
      ],
    }

    const result = await LibraryService.reserveSeat(reservationData)

    if (result.success) {
      show('Rezervasyonunuz başarıyla oluşturuldu!')
      // Verileri tekrar çekerek güncelleyelim (Optimistic update yerine re-fetch garanti çözüm)
      const freshData = await LibraryService.getAllSeats()
      const freshReservations = await LibraryService.getUserReservations()

      if (freshData) {
        setAllSeatsData(freshData)
      }
      if (freshReservations) {
        setUserReservations(freshReservations)
      }
      setSelectedSeat(null)
      // Success mesajı modal ile gösterildiği için bookingStatus'e gerek yok
    } else {
      show(result.message || 'Bir hata oluştu.')
      setBookingStatus('error')
    }
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date)
    } catch (e) {
      return dateString
    }
  }

  const handleCancelReservation = async (reservationId) => {
    if (!reservationId) return

    const result = await LibraryService.cancelReservation(reservationId)

    if (result.success) {
      console.log('result:', result)

      show('Rezervasyonunuz başarıyla iptal edildi.')
      // Listeyi güncelle
      const freshData = await LibraryService.getUserReservations()
      const freshSeats = await LibraryService.getAllSeats()

      if (freshData) setUserReservations(freshData)
      if (freshSeats) setAllSeatsData(freshSeats)
    } else {
      show(result.message || 'İptal işlemi başarısız.')
    }
  }

  const handleQrScan = async (qrData) => {
    const id = scanningContext?.id || location.state?.item?.id || 1
    const randevuIslem = scanningContext?.randevuIslem || location.state?.randevuIslem || 'onayla'

    const result = await LibraryService.processQr(qrData, id, randevuIslem)
    if (result.success) {
      // Başarılı olduğunda verileri güncelle
      getData()
    }
    return result
  }

  return (
    <div className='min-h-screen bg-[#FDFCFB]'>
      <Header title='Kütüphane' showBack />

      <div className='pb-10'>
        {/* Hero Section */}
        <div className='bg-[#AC5E2A] text-white p-8 rounded-b-[3rem] shadow-xl mb-8 relative overflow-hidden'>
          <div className='relative z-10'>
            <h1 className='text-2xl font-bold mb-2 text-white'>Prof.Dr. Mustafa Sabri KÜÇÜKAŞÇI</h1>
            <p className='text-white/80 text-sm flex items-center gap-1'>
              <MapPin size={14} /> Karatay, Konya
            </p>
          </div>
          <div className='absolute -right-10 -bottom-10 opacity-10'>
            <BookOpen size={200} />
          </div>
        </div>

        <div className='px-4 space-y-6'>
          {/* Duyurular */}
          {duyurular.length > 0 && (
            <div className='space-y-3'>
              {duyurular.map((d, i) => (
                <div key={i} className='bg-amber-50 border border-amber-200 rounded-3xl p-4 flex items-start gap-3 animate-fadeIn'>
                  <div className='w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5'>
                    <AlertCircle size={18} className='text-amber-600' />
                  </div>
                  <div>
                    <p className='text-xs font-bold text-amber-700 mb-1'>{d.sebep}</p>
                    <p className='text-xs text-amber-700 leading-relaxed'>{d.duyuru}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rezervasyonlarım Listesi */}
          <div className='bg-[#AC5E2A]/5 rounded-3xl p-3 shadow-sm border border-[#AC5E2A]/10'>
            <h2 className='text-lg font-bold text-[#AC5E2A] mb-4 text-start flex items-center gap-2'>
              <Clock size={20} />
              Rezervasyonlarım
            </h2>
            {userReservations && userReservations.length > 0 ? (
              <div className='space-y-3'>
                {userReservations.map((res, idx) => (
                  <div key={idx} className='bg-white p-4 rounded-xl border border-[#AC5E2A]/10 shadow-sm flex justify-between items-center'>
                    <div>
                      <div className='text-xs text-gray-400 font-bold mb-1 text-start'>{res.tarih}</div>
                      <div className='flex items-center gap-2'>
                        <span className='font-bold text-gray-800 text-xs'>{res.rezervasyonSaati}</span>
                      </div>
                    </div>
                    <div className='flex flex-col items-end gap-2'>
                      <div className='flex items-center gap-2'>
                        <span className='text-[10px] font-bold text-[#AC5E2A] bg-[#AC5E2A]/10 px-2 py-1 rounded-lg'>Masa {res.masaNo}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${res.durum === 'IPTAL' || res.durum === 'İptal Edildi' || res.durum === 'Cezalı' ? 'bg-red-100 text-red-600' : res.durum === 'Tamamlandı' ? 'bg-green-100 text-green-600' : res.durum === 'Aktif' || res.durum === 'Devam Ediyor' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>{res.durum}</span>
                      </div>

                      {(res.durum === 'Aktif' || res.durum === 'Devam Ediyor') && (
                        <div className='flex gap-2'>
                          {res.durum === 'Aktif' && (
                            <button
                              onClick={() => {
                                setScanningContext({
                                  id: res.id,
                                  randevuIslem: 'onayla',
                                })
                                setIsQrModalOpen(true)
                              }}
                              className='text-[10px] text-[#AC5E2A] font-bold hover:bg-[#AC5E2A]/5 px-2 py-1 rounded transition-colors border border-[#AC5E2A]/20 flex items-center gap-1'
                            >
                              <QrCode size={12} />
                              Giriş QR
                            </button>
                          )}
                          {res.durum === 'Devam Ediyor' && (
                            <button
                              onClick={() => {
                                setScanningContext({
                                  id: res.id,
                                  randevuIslem: 'ayril',
                                })
                                setIsQrModalOpen(true)
                              }}
                              className='text-[10px] text-gray-600 font-bold hover:bg-gray-50 px-2 py-1 rounded transition-colors border border-gray-200 flex items-center gap-1'
                            >
                              <QrCode size={12} />
                              Çıkış QR
                            </button>
                          )}
                          <button onClick={() => handleCancelReservation(res.id)} className='text-[10px] text-red-500 font-bold hover:bg-red-50 px-2 py-1 rounded transition-colors border border-red-100'>
                            İptal Et
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center text-gray-500 text-sm font-medium py-4'>Henüz aktif rezervasyonunuz bulunmamaktadır.</div>
            )}
          </div>

          {/* Rezervasyon Bölümü */}
          <div className='bg-white rounded-3xl p-4 shadow-sm border border-gray-50'>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-lg font-bold text-gray-800 text-start'>Masa Rezervasyonu</h2>
            </div>

            {/* Seans Seçimi */}
            <div className='mb-6'>
              <p className='text-xs font-bold text-gray-400 mb-3 text-start uppercase tracking-wider'>Seans Seçimi</p>
              <div className='grid grid-cols-2 gap-2'>
                {sessions.map((session) => (
                  <button key={session} onClick={() => setSelectedSession(session)} className={`py-3 rounded-2xl text-[10px] font-bold transition-all border ${selectedSession === session ? 'bg-[#AC5E2A] text-white border-[#AC5E2A] shadow-lg shadow-[#AC5E2A]/20' : 'bg-white text-gray-500 border-gray-100 hover:border-[#AC5E2A]/30'}`}>
                    {session
                      .split('-')
                      .map((s) => s.substring(0, 5))
                      .join(' - ')}
                  </button>
                ))}
              </div>
            </div>

            <div className='flex items-center justify-between mb-6'>
              <p className='text-xs font-bold text-gray-400 text-start uppercase tracking-wider'>Salon Seçimi</p>
              <div className='flex bg-gray-100 p-1 rounded-xl'>
                <button onClick={() => setSelectedHall(1)} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${selectedHall === 1 ? 'bg-white shadow-sm text-[#AC5E2A]' : 'text-gray-500'}`}>
                  1. SALON
                </button>
                <button onClick={() => setSelectedHall(2)} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${selectedHall === 2 ? 'bg-white shadow-sm text-[#AC5E2A]' : 'text-gray-500'}`}>
                  2. SALON
                </button>
              </div>
            </div>

            {/* Koltul Bilgilendirme */}
            <div className='flex justify-center gap-6 mb-6 text-[10px] font-medium text-gray-500'>
              <div className='flex items-center gap-1.5'>
                <div className='w-3 h-3 bg-white border border-gray-200 rounded'></div>
                <span>Müsait</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <div className='w-3 h-3 bg-red-500 rounded'></div>
                <span>Dolu</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <div className='w-3 h-3 bg-yellow-500 rounded'></div>
                <span>Seçili</span>
              </div>
            </div>

            {/* Koltuk Düzeni */}
            <div className='overflow-x-auto py-2'>
              <SeatLayout hallId={selectedHall} seats={seats} selectedSeat={selectedSeat} onSeatClick={handleSeatClick} lockedHalls={lockedHalls} />
            </div>

            {/* Rezervasyon Butonu ve Durum Mesajı */}
            <div className='mt-1 space-y-4'>
              {bookingStatus === 'success' && (
                <div className='bg-green-50 text-green-600 p-4 rounded-2xl flex items-center gap-3 text-sm font-medium animate-fadeIn'>
                  <CheckCircle2 size={18} />
                  Rezervasyonunuz başarıyla oluşturuldu!
                </div>
              )}
              {bookingStatus === 'error' && (
                <div className='bg-red-50 text-red-600 p-4 rounded-2xl flex items-center gap-3 text-sm font-medium animate-fadeIn'>
                  <AlertCircle size={18} />
                  Bir hata oluştu, lütfen tekrar deneyin.
                </div>
              )}

              {(() => {
                const lockedHallIds = lockedHalls.map((lh) => (typeof lh === 'object' ? lh.hall_id : lh))
                const isLocked = lockedHallIds.includes(selectedHall)
                return (
                  <button disabled={!selectedSeat || isLocked} onClick={handleReserve} className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all active:scale-95 ${selectedSeat && !isLocked ? 'bg-[#AC5E2A] text-white shadow-lg shadow-[#AC5E2A]/30' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                    {isLocked ? `${selectedHall}. Salon Kapalı` : selectedSeat ? `Masa ${selectedSeat} Rezervasyonunu Onayla` : 'Masa Seçiniz'}
                  </button>
                )
              })()}
            </div>
          </div>

          {/* Search Bar */}
          {/* <div className='relative'>
            <div className='absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400'>
              <Search size={18} />
            </div>
            <input
              type='text'
              placeholder='Kitap veya yazar ara...'
              className='w-full bg-white border border-gray-100 rounded-2xl py-4 pl-12 pr-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#AC5E2A]/20 transition-all'
            />
          </div> */}
        </div>
      </div>

      <QrScannerModal isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)} onScan={handleQrScan} title='Kütüphane QR Okut' description='Masadaki QR kodu veya giriş QR kodunu okutarak işleminizi tamamlayın.' />
    </div>
  )
}

export default Library
